import { Box, Stack, Typography, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DashboardSectionHeader } from "../DashboardSectionHeader"
import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MeetingList } from "./MeetingList"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchServiceCenterMeetings,
    ServiceCenterHomeQuery,
    ServiceCenterMeetingsResponse,
} from "../../serviceCenterHomeApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectUserId } from "../../../../../login/loginSelectors"
import { MeetingDrawer } from "../drawer/MettingDrawer"
import { useMemo, useState } from "react"
import { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import { ErrorBoundary } from "react-error-boundary"
import { isEmpty } from "lodash"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { UserDashboardErrorFallbackCard } from "../../../../../nonAdminUser/reports/home/components/UserDashboardErrorFallbackCard"
import { MeetingDatePicker } from "./MeetingDatePicker"
import { DateTime } from "luxon"
import { MeetingSearch } from "./MeetingSearch"

const useStyles = makeStyles()(() => ({
    card: {
        backgroundColor: "white",
        borderRadius: "0.75rem",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        padding: "1.5rem",
    },
    skeletonCard: {
        height: "2rem",
        marginBottom: ".5rem",
        borderRadius: ".5rem",
    },
    noDataContainer: {
        padding: "2rem 2rem 2rem 0",
    },
}))

function transformMeetings(data: ServiceCenterMeetingsResponse) {
    return data.orders
}

function SafeMeetingSection() {
    const { classes } = useStyles()
    const serviceCenterId = useAppSelector(selectUserId)
    const [openMeetingDetailsDrawer, setOpenMeetingDetailsDrawer] = useState(false)
    const [selectedMeeting, setSelectedMeeting] = useState<ServiceCenterBooking | null>(null)
    const [searchQuery, setSearchQuery] = useState("")

    const [selectedDate, setSelectedDate] = useState<DateTime | null>(DateTime.fromISO("2024-01-08"))

    const handleDateChange = (newDate: DateTime | null) => {
        setSelectedDate(newDate)
    }

    const { data: meetings, isLoading } = useApiQuery(fetchServiceCenterMeetings, {
        queryName: ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            startDate: selectedDate?.toISODate() ?? "",
        },
        enabled: Boolean(serviceCenterId),
        select: transformMeetings,
    })

    const filteredMeetings = useMemo(() => {
        if (!searchQuery) return meetings
        return meetings?.filter((meeting) =>
            meeting.meetingDetails.meetingName.toLowerCase().includes(searchQuery.toLowerCase()),
        )
    }, [meetings, searchQuery])

    return (
        <Box flex={1}>
            <Box className={classes.card}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="1rem">
                    <DashboardSectionHeader icon={faCalendar} badgeNumber={filteredMeetings?.length}>
                        <LocalizedStrict id="service-center-reports-dashboard-meeting-title">
                            <Typography variant="h6" fontWeight="bold">
                                Meetings
                            </Typography>
                        </LocalizedStrict>
                    </DashboardSectionHeader>

                    {!isEmpty(filteredMeetings) && (
                        <Stack direction="row" justifyContent="end" alignItems="center" spacing=".5rem">
                            <MeetingDatePicker value={selectedDate} onChange={handleDateChange} />
                            <MeetingSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                        </Stack>
                    )}
                </Stack>

                {isLoading ? (
                    <>
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    </>
                ) : !isEmpty(filteredMeetings) ? (
                    <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                        <MeetingList
                            selectedMeeting={selectedMeeting}
                            onMeetingClick={(item) => {
                                if (item.id === selectedMeeting?.id) {
                                    setOpenMeetingDetailsDrawer(false)
                                    setSelectedMeeting(null)
                                    return
                                }
                                setSelectedMeeting(item)
                                setOpenMeetingDetailsDrawer(true)
                            }}
                            meetings={filteredMeetings}
                        />
                    </ErrorBoundary>
                ) : (
                    <NoMeetingsCard />
                )}

                <MeetingDrawer
                    orderId={selectedMeeting?.id}
                    isOpen={openMeetingDetailsDrawer}
                    meetings={meetings}
                    onClose={() => {
                        setOpenMeetingDetailsDrawer(false)
                        setSelectedMeeting(null)
                    }}
                />
            </Box>
        </Box>
    )
}

export function MeetingSection() {
    return (
        <QueryErrorBoundary Fallback={MeetingErrorFallback}>
            <SafeMeetingSection />
        </QueryErrorBoundary>
    )
}

function MeetingErrorFallback() {
    const { classes } = useStyles()
    return (
        <Box flex={1}>
            <Box className={classes.card}>
                <DashboardSectionHeader icon={faCalendar}>
                    <LocalizedStrict id="service-center-reports-dashboard-meeting-title">
                        <Typography variant="h6" fontWeight="bold">
                            Meetings
                        </Typography>
                    </LocalizedStrict>
                </DashboardSectionHeader>
                <NoMeetingsCard />
            </Box>
        </Box>
    )
}

function NoMeetingsCard() {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <LocalizedStrict id="service-center-reports-dashboard-no-meetings">
                <Typography variant="subtitle1" color="#666666">
                    There are no meetings today.
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}
